import React from 'react';
import { Box, Heading, Text } from '@indeed/ifl-components';
import { PremiumBadge } from '../PremiumBadge/PremiumBadge';
import { useIsProBrandingEnabled } from '../../lib/hooks/useIsProBrandingEnabled';

interface AutoApplyHeaderProps {
    title: string;
    description?: string;
}

/**
 * Auto Apply Header Component
 *
 * Reusable header component for all auto-apply-setup pages.
 * Displays consistent branding with "Auto Apply" label, premium badge, page title, and description.
 *
 * @param title - Main page heading (e.g., "Job preferences", "Add a resume")
 * @param description - Supporting text explaining the page purpose
 *
 * Features:
 * - Consistent spacing and typography using IFL semantic tokens
 * - Premium badge integration to indicate premium feature when enabled via Indeed Pro
 * - Responsive layout with proper text hierarchy
 */
export const AutoApplyHeader: React.FC<AutoApplyHeaderProps> = ({ title, description }) => {
    const isProBrandingEnabled = useIsProBrandingEnabled();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Indeed Pro label with premium badge */}
            {isProBrandingEnabled && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <PremiumBadge />
                    <Text
                        level={2}
                        sx={{
                            fontWeight: 700,
                        }}
                    >
                        Indeed Pro
                    </Text>
                </Box>
            )}

            {/* Page heading */}
            <Heading level={5} sx={{ marginBlockEnd: 1 }}>
                {title}
            </Heading>

            {/* Page description */}
            {description && (
                <Text
                    level={2}
                    sx={{
                        fontWeight: 'typography.body.medium.fontWeight',
                    }}
                >
                    {description}
                </Text>
            )}
        </Box>
    );
};
